import { CommonModule } from '@angular/common';
import { Component, effect, input, output, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs';
import { RaIconComponent, RaSwitchComponent } from 'ra-ui';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Modal de visibilidade do documento (público/privado) — só para NORMAL/QUOTE.
 * Espelha a tela de visibilidade do app: um toggle + acesso à lista
 * "Quem pode acessar". Aberto pelo menu "mais ações" do header.
 */
@Component({
    selector: 'modal-document-visibility',
    templateUrl: 'modal-document-visibility.component.html',
    styleUrl: 'modal-document-visibility.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RaModalComponent,
        RaSwitchComponent,
        RaIconComponent,
        TranslatePipe,
    ],
})
export class ModalDocumentVisibilityComponent {
    /** Documento está privado? (fonte: o documento em edição) */
    public isPrivate = input<boolean>(false);
    /** Toggle é editável? Contratos são sempre privados (switch desabilitado). */
    public canToggle = input<boolean>(true);
    /** Loading do toggle (controlado pelo pai). */
    public loading = input<boolean>(false);
    /** Empurra o modal para o lado quando a lista de acesso abre por cima. */
    public pushed = input<boolean>(false);
    public close = output<void>();
    /** Pedido de alterar a visibilidade (público/privado). */
    public visibilityChange = output<boolean>();
    /** Pedido de abrir a lista "Quem pode acessar". */
    public openAccessList = output<void>();

    /** Controle do switch privado/público. */
    public privateControl = new FormControl<boolean>(false, { nonNullable: true });

    constructor() {
        // Reflete o `private` do documento no switch sem disparar evento. O documento
        // é realtime, então isto auto-corrige drifts/erros do servidor. Contrato é
        // sempre privado → switch desabilitado.
        effect(() => {
            const isPrivate = this.isPrivate();
            const canToggle = this.canToggle();
            untracked(() => {
                this.privateControl.setValue(isPrivate, { emitEvent: false });
                if (canToggle) {
                    this.privateControl.enable({ emitEvent: false });
                } else {
                    this.privateControl.disable({ emitEvent: false });
                }
            });
        });

        // Apenas a mudança feita pelo usuário (toggle) emite o pedido de visibilidade.
        // `distinctUntilChanged` evita o emit duplicado do RaSwitch (setValue manual +
        // onChange do CVA disparam valueChanges duas vezes com o mesmo valor).
        this.privateControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntilDestroyed())
            .subscribe(value => this.visibilityChange.emit(value));
    }
}
